/**
 * Supplier Products Page
 *
 * View and manage products linked to a specific supplier.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import {
  ArrowLeft,
  Package,
  Plus,
  Search,
  Unlink,
  Zap,
  ExternalLink,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle,
  DollarSign,
  TrendingUp,
} from 'lucide-react';

interface SupplierProduct {
  id: string;
  product_id: string;
  supplier_sku: string | null;
  supplier_product_name: string | null;
  supplier_product_url: string | null;
  supplier_cost: number;
  supplier_currency: string;
  shipping_cost: number;
  markup_type: string | null;
  markup_value: number | null;
  is_primary: boolean;
  auto_fulfill: boolean;
  status: string;
  product: {
    id: string;
    name: string;
    slug: string;
    price: number;
    images: string[];
    status: string;
  };
  profit_margin: number | null;
}

interface Supplier {
  id: string;
  name: string;
}

interface StoreProduct {
  id: string;
  name: string;
  price: number;
  images: string[];
  status: string;
}

export default function SupplierProductsPage() {
  const { t } = useTranslation('store');
  const { storeId, supplierId } = useParams<{ storeId: string; supplierId: string }>();

  const [supplierProducts, setSupplierProducts] = useState<SupplierProduct[]>([]);
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Link product modal
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<StoreProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [linkFormData, setLinkFormData] = useState({
    supplier_sku: '',
    supplier_product_url: '',
    supplier_cost: '',
    shipping_cost: '',
    is_primary: true,
    auto_fulfill: true,
  });

  // Import modal
  const [showImportModal, setShowImportModal] = useState(false);

  const loadSupplierProducts = useCallback(async () => {
    if (!storeId || !supplierId) return;

    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.set('search', search);

      const response = await api.get(`/api/ecommerce/stores/${storeId}/suppliers/${supplierId}/products?${params}`);
      const data = await response.json() as { supplier_products: SupplierProduct[]; supplier: Supplier };
      setSupplierProducts(data.supplier_products || []);
      setSupplier(data.supplier);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [storeId, supplierId, search]);

  const loadAvailableProducts = useCallback(async () => {
    if (!storeId) return;

    try {
      const response = await api.get(`/api/ecommerce/stores/${storeId}/products?limit=100`);
      const data = await response.json() as { products: StoreProduct[] };
      const linkedProductIds = new Set(supplierProducts.map(sp => sp.product_id));
      setAvailableProducts((data.products || []).filter(p => !linkedProductIds.has(p.id)));
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  }, [storeId, supplierProducts]);

  useEffect(() => {
    loadSupplierProducts();
  }, [loadSupplierProducts]);

  useEffect(() => {
    if (showLinkModal) {
      loadAvailableProducts();
    }
  }, [showLinkModal, loadAvailableProducts]);

  const handleLinkProduct = async () => {
    if (!storeId || !supplierId || !selectedProduct) return;

    try {
      setSaving(true);
      await api.post(`/api/ecommerce/stores/${storeId}/suppliers/${supplierId}/products`, {
        product_id: selectedProduct,
        supplier_sku: linkFormData.supplier_sku || null,
        supplier_product_url: linkFormData.supplier_product_url || null,
        supplier_cost: parseInt(linkFormData.supplier_cost) || 0,
        shipping_cost: parseInt(linkFormData.shipping_cost) || 0,
        is_primary: linkFormData.is_primary,
        auto_fulfill: linkFormData.auto_fulfill,
      });
      setShowLinkModal(false);
      setSelectedProduct('');
      setLinkFormData({ supplier_sku: '', supplier_product_url: '', supplier_cost: '', shipping_cost: '', is_primary: true, auto_fulfill: true });
      setSuccess(t('suppliers.productLinked', 'Product linked successfully'));
      loadSupplierProducts();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link product');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlinkProduct = async (productId: string) => {
    if (!storeId || !supplierId) return;

    if (!confirm(t('suppliers.confirmUnlink', 'Are you sure you want to unlink this product?'))) return;

    try {
      await api.delete(`/api/ecommerce/stores/${storeId}/suppliers/${supplierId}/products/${productId}`);
      setSuccess(t('suppliers.productUnlinked', 'Product unlinked'));
      loadSupplierProducts();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlink product');
    }
  };

  const formatCurrency = (cents: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(cents / 100);
  };

  const calculateProfitPercent = (sellPrice: number, cost: number, shipping: number) => {
    const totalCost = cost + shipping;
    if (totalCost <= 0) return null;
    return Math.round(((sellPrice - totalCost) / sellPrice) * 100);
  };

  if (loading && supplierProducts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link
            to={`/store/${storeId}/suppliers/${supplierId}`}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Package className="w-6 h-6" />
              {supplier?.name} - {t('suppliers.products', 'Products')}
            </h1>
            <p className="text-gray-500">
              {t('suppliers.manageLinkedProducts', 'Manage products linked to this supplier')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Upload className="w-4 h-4" />
            {t('suppliers.importProducts', 'Import')}
          </button>
          <button
            onClick={() => setShowLinkModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            {t('suppliers.linkProduct', 'Link Product')}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('suppliers.searchProducts', 'Search products...')}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">×</button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Products List */}
      {supplierProducts.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('suppliers.noLinkedProducts', 'No products linked')}
          </h3>
          <p className="text-gray-500 mb-4">
            {t('suppliers.linkProductsDescription', 'Link existing products or import from supplier')}
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => setShowImportModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Upload className="w-4 h-4" />
              {t('suppliers.importProducts', 'Import')}
            </button>
            <button
              onClick={() => setShowLinkModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              {t('suppliers.linkProduct', 'Link Product')}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('suppliers.product', 'Product')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('suppliers.supplierSku', 'Supplier SKU')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('suppliers.cost', 'Cost')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('suppliers.sellPrice', 'Sell Price')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('suppliers.profit', 'Profit')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('suppliers.status', 'Status')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('suppliers.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {supplierProducts.map((sp) => {
                const profitPercent = calculateProfitPercent(sp.product.price, sp.supplier_cost, sp.shipping_cost);
                const profitAmount = sp.product.price - sp.supplier_cost - sp.shipping_cost;

                return (
                  <tr key={sp.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                          {sp.product.images[0] ? (
                            <img src={sp.product.images[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Package className="w-full h-full p-2 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <Link
                            to={`/store/${storeId}/products/${sp.product_id}`}
                            className="font-medium text-gray-900 hover:text-blue-600"
                          >
                            {sp.product.name}
                          </Link>
                          {sp.supplier_product_url && (
                            <a
                              href={sp.supplier_product_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 mt-0.5"
                            >
                              {t('suppliers.viewOnSupplier', 'View on supplier')}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {sp.supplier_sku || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div>{formatCurrency(sp.supplier_cost, sp.supplier_currency)}</div>
                      {sp.shipping_cost > 0 && (
                        <div className="text-xs text-gray-500">
                          +{formatCurrency(sp.shipping_cost, sp.supplier_currency)} shipping
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {formatCurrency(sp.product.price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium ${profitAmount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(profitAmount)}
                      </div>
                      {profitPercent !== null && (
                        <div className={`text-xs ${profitPercent > 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {profitPercent > 0 ? '+' : ''}{profitPercent}%
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {sp.is_primary && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                            Primary
                          </span>
                        )}
                        {sp.auto_fulfill && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full flex items-center gap-1">
                            <Zap className="w-3 h-3" /> Auto
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleUnlinkProduct(sp.product_id)}
                        className="text-red-600 hover:text-red-700 p-2"
                        title={t('suppliers.unlinkProduct', 'Unlink')}
                      >
                        <Unlink className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary Stats */}
      {supplierProducts.length > 0 && (
        <div className="mt-6 grid grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{supplierProducts.length}</div>
                <div className="text-sm text-gray-500">{t('suppliers.totalProducts', 'Total Products')}</div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {formatCurrency(supplierProducts.reduce((sum, sp) => sum + sp.supplier_cost, 0))}
                </div>
                <div className="text-sm text-gray-500">{t('suppliers.totalCost', 'Total Cost')}</div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {formatCurrency(supplierProducts.reduce((sum, sp) => sum + (sp.product.price - sp.supplier_cost - sp.shipping_cost), 0))}
                </div>
                <div className="text-sm text-gray-500">{t('suppliers.potentialProfit', 'Potential Profit')}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Link Product Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">{t('suppliers.linkProduct', 'Link Product')}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('suppliers.selectProduct', 'Select Product')} *
                </label>
                <select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('suppliers.chooseProduct', 'Choose a product...')}</option>
                  {availableProducts.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} - {formatCurrency(p.price)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('suppliers.supplierSku', 'Supplier SKU')}
                </label>
                <input
                  type="text"
                  value={linkFormData.supplier_sku}
                  onChange={(e) => setLinkFormData({ ...linkFormData, supplier_sku: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., ALI-12345"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('suppliers.supplierUrl', 'Supplier Product URL')}
                </label>
                <input
                  type="url"
                  value={linkFormData.supplier_product_url}
                  onChange={(e) => setLinkFormData({ ...linkFormData, supplier_product_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.supplierCost', 'Cost (cents)')} *
                  </label>
                  <input
                    type="number"
                    value={linkFormData.supplier_cost}
                    onChange={(e) => setLinkFormData({ ...linkFormData, supplier_cost: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="1000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.shippingCost', 'Shipping (cents)')}
                  </label>
                  <input
                    type="number"
                    value={linkFormData.shipping_cost}
                    onChange={(e) => setLinkFormData({ ...linkFormData, shipping_cost: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="200"
                  />
                </div>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={linkFormData.is_primary}
                    onChange={(e) => setLinkFormData({ ...linkFormData, is_primary: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-sm">{t('suppliers.primarySupplier', 'Primary')}</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={linkFormData.auto_fulfill}
                    onChange={(e) => setLinkFormData({ ...linkFormData, auto_fulfill: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-sm">{t('suppliers.autoFulfill', 'Auto-Fulfill')}</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowLinkModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleLinkProduct}
                disabled={saving || !selectedProduct || !linkFormData.supplier_cost}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? t('common.saving', 'Saving...') : t('suppliers.linkProduct', 'Link Product')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <h2 className="text-xl font-semibold mb-4">{t('suppliers.importProducts', 'Import Products')}</h2>
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                {t('suppliers.importDescription', 'Import products from your supplier using CSV or API integration.')}
              </p>

              <div className="grid grid-cols-2 gap-4">
                <Link
                  to={`/store/${storeId}/suppliers/${supplierId}?tab=settings`}
                  className="flex flex-col items-center gap-2 p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  onClick={() => setShowImportModal(false)}
                >
                  <div className="p-3 bg-blue-100 rounded-full">
                    <Zap className="w-6 h-6 text-blue-600" />
                  </div>
                  <span className="font-medium">{t('suppliers.apiImport', 'API Import')}</span>
                  <span className="text-xs text-gray-500 text-center">
                    {t('suppliers.apiImportDescription', 'Configure API settings first')}
                  </span>
                </Link>

                <Link
                  to={`/store/${storeId}/suppliers/${supplierId}/import`}
                  className="flex flex-col items-center gap-2 p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors"
                  onClick={() => setShowImportModal(false)}
                >
                  <div className="p-3 bg-green-100 rounded-full">
                    <FileSpreadsheet className="w-6 h-6 text-green-600" />
                  </div>
                  <span className="font-medium">{t('suppliers.csvImport', 'CSV Import')}</span>
                  <span className="text-xs text-gray-500 text-center">
                    {t('suppliers.csvImportDescription', 'Upload a CSV file')}
                  </span>
                </Link>
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
